import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, CheckCircle, XCircle, AlertTriangle, Clock,
  RefreshCw, ChevronRight, ExternalLink,
} from 'lucide-react';
import { api } from '../../services/api';
import ComplianceTrendChart from './ComplianceTrendChart';

// ── Types ─────────────────────────────────────────────────────────────────────

interface SkopeScan {
  id: string;
  entity_id: string | null;
  project_id: string | null;
  target_url: string;
  status: 'running' | 'completed' | 'failed' | 'pending';
  compliance_score: number | null;
  violations_count: number | null;
  warnings_count: number | null;
  passes_count: number | null;
  wcag_level: string | null;
  created_at: string | null;
  completed_at: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });
}

function hostname(url: string): string {
  try { return new URL(url).hostname; } catch { return url; }
}

function complianceColor(score: number | null): string {
  if (score == null) return 'text-slate-400';
  if (score >= 90) return 'text-emerald-400';
  if (score >= 70) return 'text-yellow-400';
  return 'text-red-400';
}

const STATUS_CONFIG = {
  completed: { label: 'Completado', icon: CheckCircle,  cls: 'text-emerald-400 bg-emerald-400/10' },
  failed:    { label: 'Fallido',    icon: XCircle,      cls: 'text-red-400 bg-red-400/10' },
  running:   { label: 'Ejecutando', icon: RefreshCw,    cls: 'text-blue-400 bg-blue-400/10' },
  pending:   { label: 'Pendiente',  icon: Clock,        cls: 'text-slate-400 bg-slate-400/10' },
};

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  entityId: string;
  projectId: string;
}

export default function SkopeScansTab({ entityId, projectId }: Props) {
  const navigate = useNavigate();
  const [scans, setScans]     = useState<SkopeScan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [total, setTotal]     = useState(0);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listScans({ entity_id: entityId, project_id: projectId, page_size: 50 });
      setScans(res.items ?? res ?? []);
      setTotal(res.total ?? (res.items ?? res ?? []).length);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Error al cargar scans');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [entityId, projectId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex items-center gap-3 text-slate-400">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Cargando scans de accesibilidad...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400">
        <AlertTriangle className="w-5 h-5 shrink-0" />
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  if (scans.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="p-4 bg-slate-800 rounded-full mb-4">
          <Shield className="w-8 h-8 text-slate-500" />
        </div>
        <h3 className="text-lg font-semibold text-white mb-2">Sin scans de accesibilidad</h3>
        <p className="text-slate-400 text-sm max-w-sm mb-4">
          Ejecuta un scan con Skope CLI para analizar esta entidad con WCAG/ARIA.
        </p>
        <code className="text-xs bg-slate-800 text-violet-300 px-3 py-2 rounded-lg">
          skope scan --entity {entityId}
        </code>
      </div>
    );
  }

  return (
    <div>
      {/* Trend Chart */}
      <ComplianceTrendChart entityId={entityId} projectId={projectId} />

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-violet-400" />
          <h3 className="text-sm font-semibold text-white">
            Scans de Accesibilidad
            <span className="ml-2 text-xs text-slate-500 font-normal">({total} scans)</span>
          </h3>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white px-2.5 py-1.5
                     bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Actualizar
        </button>
      </div>

      {/* Table */}
      <div className="border border-slate-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-900/40">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">URL</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">WCAG</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Compliance</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Violations</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Warnings</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Passes</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Estado</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Fecha</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {scans.map(scan => {
              const st = STATUS_CONFIG[scan.status] ?? STATUS_CONFIG.pending;
              const StatusIcon = st.icon;
              return (
                <tr
                  key={scan.id}
                  onClick={() => navigate(`/scans/${scan.id}`)}
                  className="hover:bg-slate-800/40 cursor-pointer transition-colors"
                >
                  {/* URL */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <ExternalLink className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                      <span className="text-sm text-white font-medium truncate max-w-[200px]">
                        {hostname(scan.target_url)}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 truncate max-w-[200px] pl-5">
                      {scan.target_url}
                    </p>
                  </td>
                  {/* WCAG Level */}
                  <td className="px-4 py-3 text-center">
                    {scan.wcag_level ? (
                      <span className="text-xs px-2 py-1 bg-violet-500/10 text-violet-300 border border-violet-500/20 rounded-md font-mono">
                        {scan.wcag_level}
                      </span>
                    ) : (
                      <span className="text-slate-600 text-xs">—</span>
                    )}
                  </td>
                  {/* Compliance */}
                  <td className="px-4 py-3 text-right">
                    <span className={`text-lg font-bold font-mono ${complianceColor(scan.compliance_score)}`}>
                      {scan.compliance_score != null ? `${scan.compliance_score.toFixed(0)}%` : '—'}
                    </span>
                  </td>
                  {/* Violations */}
                  <td className="px-4 py-3 text-center">
                    <span className={`text-sm font-semibold font-mono ${
                      scan.violations_count == null ? 'text-slate-500' :
                      scan.violations_count === 0    ? 'text-emerald-400' :
                      scan.violations_count < 5      ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      {scan.violations_count ?? '—'}
                    </span>
                  </td>
                  {/* Warnings */}
                  <td className="px-4 py-3 text-center">
                    <span className={`text-sm font-mono ${
                      scan.warnings_count == null ? 'text-slate-500' :
                      scan.warnings_count === 0   ? 'text-emerald-400' : 'text-yellow-400'
                    }`}>
                      {scan.warnings_count ?? '—'}
                    </span>
                  </td>
                  {/* Passes */}
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm font-mono text-emerald-400">
                      {scan.passes_count ?? '—'}
                    </span>
                  </td>
                  {/* Status */}
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full font-medium ${st.cls}`}>
                      <StatusIcon className={`w-3 h-3 ${scan.status === 'running' ? 'animate-spin' : ''}`} />
                      {st.label}
                    </span>
                  </td>
                  {/* Date */}
                  <td className="px-4 py-3 text-right text-xs text-slate-500">
                    {fmtDate(scan.completed_at ?? scan.created_at)}
                  </td>
                  {/* Arrow */}
                  <td className="px-4 py-3 text-slate-600">
                    <ChevronRight className="w-4 h-4" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
